import React from 'react';
import styled from 'styled-components';
import { Container } from '@nfs-react/components';

import { useImg, useText } from 'hooks';
import { MOBILE, TABLET, TO_DESKTOP } from 'helpers/styles/media';
import { ChangeableBackgroundSection } from 'helpers/components';

const Footer = () => {
  const serviceLink = [
    {
      icon: useImg('mobile_phone.svg', 'mobile_phone'),
      title: useText('service_link_1', true),
    },
    {
      icon: useImg('microphone.svg', 'microphone'),
      title: useText('service_link_2', true),
    },
    {
      icon: useImg('email_open.svg', 'email_open'),
      title: useText('service_link_3', true),
    },
    {
      icon: useImg('email.svg', 'email'),
      title: useText('service_link_4', true),
    },
  ];

  const navLink = [
    useText('footer_link_nav_1', true),
    useText('footer_link_nav_2', true),
    useText('footer_link_nav_3', true),
    useText('footer_link_nav_4', true),
    useText('footer_link_nav_5', true),
    useText('footer_link_nav_6', true),
    useText('footer_link_nav_7', true),
    useText('footer_link_nav_8', true),
    useText('footer_link_nav_9', true),
    useText('footer_link_nav_10', true),
    useText('footer_link_nav_11', true),
    useText('footer_link_nav_12', true),
  ];

  const mainNavLink = [
    useText('footer_main_nav_link_1', true),
    useText('footer_main_nav_link_2', true),
    useText('footer_main_nav_link_3', true),
    useText('footer_main_nav_link_4', true),
    useText('footer_main_nav_link_5', true),
    useText('footer_main_nav_link_6', true),
    useText('footer_main_nav_link_7', true),
    useText('footer_main_nav_link_8', true),
    useText('footer_main_nav_link_9', true),
    useText('footer_main_nav_link_10', true),
    useText('footer_main_nav_link_11', true),
  ];

  return (
    <ChangeableBackgroundSection
      id="footer-wrapper-bg"
      defaultStyle={{ background: '#141414' }}
    >
      <ServiceContainer>
        <TitleList>{useText('footer_title', true)}</TitleList>
        <ServiceLink>
          {serviceLink.map(({ icon, title }, index) => {
            return (
              <Item key={index}>
                {icon}
                {title}
              </Item>
            );
          })}
        </ServiceLink>
      </ServiceContainer>
      <Line />
      <FooterContainer>
        <GlobalFooter>
          <GlobalNav>
            {navLink.map((el, index) => {
              return (
                <Item1 key={index}>{el}</Item1>
              );
            })}
          </GlobalNav>
          <MainNav>
            {mainNavLink.map((elem, index) => {
              return (
                <Item2 key={index}>{elem}</Item2>
              );
            })}
          </MainNav>
          <Text>{useText('footer_text', true)}</Text>
        </GlobalFooter>
      </FooterContainer>
    </ChangeableBackgroundSection>
  );
};

const ServiceContainer = styled(Container)`
  color: #fff;
  padding: 16px 10px;

  @media ${TABLET} {
    max-width: 1200px;
    padding: 16px 30px;
  }

  @media ${MOBILE} {
    max-width: 768px;
    padding: 16px 20px;
  }
`;

const TitleList = styled.div`
  h2 {
    margin: 0;
    font-size: 20px;
    line-height: 24px;
    font-weight: 700;
    padding-bottom: 12px;
  }

  @media ${MOBILE} {
    font-size: 18px;
    line-height: 22px;
  }
`;

const ServiceLink = styled.div`
  display: flex;

  @media ${MOBILE} {
    flex-wrap: wrap;
    row-gap: 20px;
    justify-content: space-between;
  }
`;

const Item = styled.div`
  width: 25%;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  line-height: 16px;
  cursor: pointer;

  a {
    color: #fff;
    text-decoration: none;
  }

  div:last-child {
    border-bottom: 1px solid transparent;

    :hover {
      border-bottom: 1px solid #fff;
    }
  }

  @media ${MOBILE} {
    width: auto;
    min-width: 250px;
  }
`;

const Line = styled.div`
  height: 1px;
  background-color: #8a8c8e;
`;

const FooterContainer = styled(Container)`
  padding: 0 10px;

  @media ${TABLET} {
    max-width: 1200px;
    padding: 0 30px;
  }

  @media ${MOBILE} {
    max-width: 768px;
    padding: 0 20px;
  }
`;

const GlobalFooter = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 1200px;
  margin: 0 auto;
`;

const GlobalNav = styled.div`
  padding: 16px 0;
  flex-wrap: wrap;
  column-rule: 2px solid #8a8c8e;
  columns: 8rem;
  gap: 2.5rem;
  vertical-align: baseline;
  font: inherit;
  border: 0;

  @media ${TABLET} {
    columns: 6.5rem;
    gap: 2.5rem;
  }

  @media ${MOBILE} {
    columns: 5.625rem;
    gap: 1.5rem;
  }
`;

const Item1 = styled.div`
  min-width: 144px;
  font-size: 13px;
  line-height: 16px;
  padding-block: 12px;
  display: block;
  cursor: pointer;

  a {
    color: #fff;
    text-decoration: none;
  }

  @media ${TO_DESKTOP} {
    min-width: 90px;
    width: 100%;
    max-width: 144px;
  }
`;

const MainNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  border-top: 1px solid #8a8c8e;
  column-gap: 24px;
`;

const Item2 = styled.a`
  font-size: 13px;
  line-height: 16px;
  padding: 12px 0;
  cursor: pointer;

  a {
    color: #fff;
    text-decoration: none;
  }
`;

const Text = styled.div`
  color: #f6f6f6;
  font-size: 13px;
  line-height: 16px;
  font-weight: 400;
  margin: 24px 0 16px;

  a {
    color: #fff;
    font-weight: 700;
    text-decoration: none;
    cursor: pointer;
  }
`;

export default Footer;
